import { useLayoutEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { sanitizeRichHtml } from '@/lib/richtext'

interface Props {
  /** Current HTML. The editor is uncontrolled internally (contentEditable
   *  + React state don't mix well — re-assigning innerHTML on every render
   *  kills the caret). We only push `value` into the DOM when it differs
   *  from the last value we EMITTED — i.e. external resets. */
  value: string
  onChange: (html: string) => void
  /** Placeholder shown when the editor is empty (CSS :empty::before). */
  placeholder?: string
  className?: string
  /** Optional ref to focus / inspect the underlying element. */
  innerRef?: React.MutableRefObject<HTMLDivElement | null>
  ariaLabel?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  spellCheck?: boolean
}

/** A small contentEditable rich-text editor that PRESERVES pasted formatting
 *  (colors, fonts, backgrounds, highlights…) by accepting HTML on paste and
 *  sanitizing it through DOMPurify. Stores HTML; the parent decides how to
 *  render it (usually via dangerouslySetInnerHTML + sanitizeRichHtml). */
export function RichEditor({
  value,
  onChange,
  placeholder,
  className,
  innerRef,
  ariaLabel,
  onKeyDown,
  spellCheck = true,
}: Props) {
  const elRef = useRef<HTMLDivElement | null>(null)
  // Last HTML we emitted upward. We use this to detect EXTERNAL value
  // changes (a reset / a programmatic clear) vs the natural editor state.
  const lastEmittedRef = useRef<string>(value)
  // Track whether we've performed the initial innerHTML seed. The compare-
  // to-last-emitted trick deliberately skips re-assignment to preserve the
  // caret on every keystroke, but it ALSO skipped the very first paint if
  // the parent passed a non-empty initial value — the editor stayed blank
  // until the user typed something. This ref forces a one-time seed on
  // mount regardless of the compare.
  const seededRef = useRef(false)

  function setRef(node: HTMLDivElement | null) {
    elRef.current = node
    if (innerRef) innerRef.current = node
  }

  useLayoutEffect(() => {
    const el = elRef.current
    if (!el) return
    if (!seededRef.current) {
      el.innerHTML = sanitizeRichHtml(value)
      lastEmittedRef.current = value
      seededRef.current = true
      return
    }
    if (value !== lastEmittedRef.current) {
      el.innerHTML = sanitizeRichHtml(value)
      lastEmittedRef.current = value
    }
  }, [value])

  function emit() {
    const el = elRef.current
    if (!el) return
    let html = el.innerHTML
    // Treat a lone <br> (browsers insert one on empty contentEditable) as
    // genuinely empty so the placeholder kicks in via :empty.
    if (html === '<br>' || html === '<br/>') html = ''
    lastEmittedRef.current = html
    onChange(html)
  }

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault()
    const cb = e.clipboardData
    const html = cb.getData('text/html')
    const text = cb.getData('text/plain')
    const insert = html
      ? sanitizeRichHtml(html)
      : escapeText(text)
    // execCommand('insertHTML') is deprecated but universally supported and
    // the simplest way to paste at the current caret without re-implementing
    // a full Selection/Range insertion.
    document.execCommand('insertHTML', false, insert)
    emit()
  }

  return (
    <div
      ref={setRef}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={ariaLabel}
      spellCheck={spellCheck}
      onInput={emit}
      onBlur={emit}
      onPaste={handlePaste}
      onKeyDown={onKeyDown}
      // File drops MUST bubble out of the editor — without these handlers,
      // contentEditable's default would insert the file as an inline <img>
      // / data URL right in the body instead of letting the parent wrapper
      // stage it as a comment attachment. We block the default insertion
      // for file drags but otherwise leave the event alone so the wrapper
      // catches it via the bubble. The detection accepts Files in `types`,
      // any file-kind item in `items`, OR a non-empty `files` list — some
      // browsers (and in-page drag sources) populate only one of those.
      onDragOver={(e) => {
        const dt = e.dataTransfer
        const isFile =
          Array.from(dt?.types ?? []).includes('Files') ||
          (dt?.items && Array.from(dt.items).some((i) => i.kind === 'file')) ||
          (dt?.files?.length ?? 0) > 0
        if (isFile) e.preventDefault()
      }}
      onDrop={(e) => {
        const dt = e.dataTransfer
        const isFile =
          Array.from(dt?.types ?? []).includes('Files') ||
          (dt?.items && Array.from(dt.items).some((i) => i.kind === 'file')) ||
          (dt?.files?.length ?? 0) > 0
        if (isFile) e.preventDefault()
      }}
      data-placeholder={placeholder}
      className={cn('rich-editor', className)}
    />
  )
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>')
}
